import * as Sentry from '@sentry/react'
import React from 'react'

// Initialize Sentry for error tracking and performance monitoring
export const initializeMonitoring = () => {
  // Only initialize in production or when explicitly enabled
  if (import.meta.env.PROD || import.meta.env.VITE_ENABLE_MONITORING === 'true') {
    const dsn = import.meta.env.VITE_SENTRY_DSN

    // Skip initialization if DSN is not configured
    if (!dsn || dsn === 'your_sentry_dsn_here') {
      console.warn('Sentry DSN not configured. Error monitoring is disabled.')
      return
    }

    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      integrations: [
        // React Router integration
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect: React.useEffect,
        }),
        // Web Vitals for performance monitoring
        Sentry.replayIntegration({
          maskAllText: false, // Set to true in production for privacy
          blockAllMedia: true,
        }),
      ],
      
      // Performance monitoring
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in prod, 100% in dev
      
      // Session replay
      replaysSessionSampleRate: 0.1, // 10% of sessions
      replaysOnErrorSampleRate: 1.0, // 100% of error sessions
      
      // Release tracking
      release: import.meta.env.VITE_APP_VERSION || '1.0.0',
      
      // Configure which errors to capture
      beforeSend(event, hint) {
        // Filter out non-critical errors
        if (event.exception) {
          const error = hint.originalException
          
          // Skip network errors that are expected
          if (error instanceof Error) {
            if (error.message.includes('NetworkError') || 
                error.message.includes('Failed to fetch')) {
              return null
            }
          }
        }
        
        return event
      },
      
      // Initial user context
      initialScope: {
        tags: {
          component: 'facility-pro'
        },
        contexts: {
          app: {
            name: 'AI Facility Manager',
            version: import.meta.env.VITE_APP_VERSION || '1.0.0'
          }
        }
      }
    })
  }
}

// User action logging
export interface UserAction {
  action: string
  category: 'auth' | 'booking' | 'payment' | 'member' | 'invoice' | 'communication' | 'navigation'
  details?: Record<string, any>
  userId?: string
  timestamp?: number
}

export const logUserAction = (userAction: UserAction) => {
  const actionData = {
    ...userAction,
    timestamp: userAction.timestamp || Date.now()
  }
  
  // Send to Sentry as breadcrumb
  Sentry.addBreadcrumb({
    message: `User action: ${userAction.action}`,
    category: userAction.category,
    data: actionData,
    level: 'info'
  })
  
  // Also log to console in development
  if (import.meta.env.DEV) {
    console.log('📊 User Action:', actionData)
  }
}

// Performance monitoring utilities
export const startTransaction = (name: string, operation: string) => {
  // Use modern Sentry performance API
  return Sentry.startSpan({
    name,
    op: operation,
    attributes: {
      'facility-pro': true
    }
  }, () => {})
}

// Critical error logging
export const logCriticalError = (error: Error, context: Record<string, any> = {}) => {
  Sentry.withScope((scope) => {
    scope.setLevel('error')
    scope.setContext('error_context', context)
    scope.setTag('critical', true)
    Sentry.captureException(error)
  })
}

// User context management
export const setUserContext = (user: {
  id: string
  email?: string
  role?: string
  memberNumber?: string
}) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.memberNumber,
    role: user.role
  })
}

// Clear user context on logout
export const clearUserContext = () => {
  Sentry.setUser(null)
}

// Performance measurement for specific operations
export const measurePerformance = async <T>(
  name: string,
  operation: () => Promise<T>,
  category: string = 'operation'
): Promise<T> => {
  const startTime = performance.now()
  
  return Sentry.startSpan({
    name,
    op: category,
    attributes: {
      'facility-pro': true
    }
  }, async () => {
    try {
      const result = await operation()
      const duration = performance.now() - startTime
      
      // Log performance data
      Sentry.addBreadcrumb({
        message: `Performance: ${name}`,
        category: 'performance',
        data: {
          duration,
          operation: name
        },
        level: 'info'
      })
      
      return result
    } catch (error) {
      const duration = performance.now() - startTime
      
      logCriticalError(error as Error, {
        operation: name,
        duration,
        category
      })
      
      throw error
    }
  })
}

// Business logic specific logging
export const logBusinessEvent = (event: {
  type: 'booking_created' | 'payment_processed' | 'invoice_sent' | 'member_registered' | 'facility_conflict'
  userId?: string
  data: Record<string, any>
}) => {
  Sentry.addBreadcrumb({
    message: `Business event: ${event.type}`,
    category: 'business',
    data: {
      ...event.data,
      userId: event.userId,
      timestamp: Date.now()
    },
    level: 'info'
  })
  
  // Set custom event for business intelligence
  Sentry.setTag('business_event', event.type)
}